import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader, CTABand } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { BLOG } from "../data";
import { Search, ArrowUpRight, Clock } from "lucide-react";

const CATEGORIES = ["All", ...Array.from(new Set(BLOG.map((b) => b.category)))];

export default function Blog() {
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const filtered = BLOG.filter((b) =>
    (cat === "All" || b.category === cat) &&
    (query === "" || (b.title + b.excerpt).toLowerCase().includes(query.toLowerCase()))
  );
  const [featured, ...rest] = filtered.length ? filtered : [null];

  return (
    <div data-testid="blog-page">
      <PageHeader overline="Insights & News" title="The Hirezen desk" subtitle="Field notes on hiring, retention, and the future of work — written by the recruiters in the trenches." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Controls */}
          <Reveal className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-10">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c} data-testid={`blog-cat-${c.toLowerCase().replace(/ /g, "-")}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${cat === c ? "bg-amber-400 text-slate-950" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>{c}</button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="blog-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search articles…" className="input-dark pl-10" />
            </div>
          </Reveal>

          {featured && (
            <Reveal className="mb-8">
              <button onClick={() => setActive(featured)} data-testid={`blog-featured`} className="group grid grid-cols-1 lg:grid-cols-2 gap-0 w-full text-left rounded-3xl overflow-hidden border border-slate-800/80 bg-[#111827]/60 hover:border-amber-500/40 transition-all">
                <div className="relative h-64 lg:h-full min-h-[280px] overflow-hidden"><img src={featured.img} alt={featured.title} className="w-full h-full object-cover opacity-75 group-hover:opacity-95 group-hover:scale-105 transition-all duration-700" /><span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest bg-[#0B0F19]/80 border border-slate-700 text-amber-400 rounded-full px-3 py-1">Featured · {featured.category}</span></div>
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <div className="font-mono text-xs text-slate-500 mb-3">{featured.date} · {featured.read} read</div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight group-hover:text-amber-400 transition-colors mb-4">{featured.title}</h2>
                  <p className="text-slate-400 leading-relaxed mb-6">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 group-hover:gap-2.5 transition-all">Read article <ArrowUpRight className="w-4 h-4" /></span>
                </div>
              </button>
            </Reveal>
          )}

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" key={cat + query}>
            {rest.map((b) => (
              <StaggerItem key={b.id}>
                <button onClick={() => setActive(b)} data-testid={`blog-card-${b.id}`} className="group text-left w-full h-full rounded-2xl overflow-hidden border border-slate-800/80 bg-[#111827]/60 hover-lift hover:border-amber-500/40 flex flex-col">
                  <div className="relative h-44 overflow-hidden"><img src={b.img} alt={b.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" /><span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-[#0B0F19]/80 border border-slate-700 text-amber-400 rounded-full px-2.5 py-1">{b.category}</span></div>
                  <div className="p-6 flex flex-col flex-1"><div className="font-mono text-xs text-slate-500 mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{b.read} · {b.date}</div><h3 className="text-lg font-semibold text-white leading-snug group-hover:text-amber-400 transition-colors mb-2">{b.title}</h3><p className="text-sm text-slate-400 leading-relaxed">{b.excerpt}</p></div>
                </button>
              </StaggerItem>
            ))}
          </Stagger>
          {filtered.length === 0 && <Reveal className="text-center py-20 text-slate-500">No articles found.</Reveal>}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-2xl p-0 overflow-hidden" data-testid="blog-article-dialog">
          {active && (
            <div className="max-h-[85vh] overflow-y-auto">
              <div className="relative h-56"><img src={active.img} alt={active.title} className="w-full h-full object-cover opacity-70" /><div className="absolute inset-0 bg-gradient-to-t from-[#111827] to-transparent" /><span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest bg-[#0B0F19]/80 border border-slate-700 text-amber-400 rounded-full px-3 py-1">{active.category}</span></div>
              <div className="p-8">
                <div className="font-mono text-xs text-slate-500 mb-3">By {active.author} · {active.date} · {active.read} read</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-5">{active.title}</h2>
                <div className="space-y-4">{active.body.split("\n\n").map((p, i) => <p key={i} className="text-slate-300 leading-relaxed">{p}</p>)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTABand />
    </div>
  );
}
